import type React from "react";
import { useCallback } from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface PercentageInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

function sanitize(raw: string): string {
  let result = "";
  let hasDot = false;
  for (let i = 0; i < raw.length; i++) {
    const ch = raw[i];
    if (ch === "-" && i === 0) {
      result += ch;
    } else if (ch === "." && !hasDot) {
      hasDot = true;
      result += ch;
    } else if (ch >= "0" && ch <= "9") {
      result += ch;
    }
  }
  return result;
}

function formatToTwoDecimals(val: string): string {
  if (!val || val === "-") return val;
  const num = parseFloat(val);
  if (isNaN(num)) return "";
  return num.toFixed(2);
}

function PercentageInput({
  value,
  onChange,
  placeholder = "0.00",
  className,
  disabled,
}: PercentageInputProps) {
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(sanitize(e.target.value));
    },
    [onChange],
  );

  const handleBlur = useCallback(() => {
    if (value) {
      onChange(formatToTwoDecimals(value));
    }
  }, [value, onChange]);

  return (
    <div className="relative">
      <Input
        type="text"
        inputMode="decimal"
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        className={cn("pr-7", className)}
      />
      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm pointer-events-none">
        %
      </span>
    </div>
  );
}

export { PercentageInput };
export type { PercentageInputProps };
